<div class="card">
  @if (canDownloadCsv || actions.length > 0 || hasAnyFilter) {
    <div class="card-header">
      <div class="card-menus">
        @if (hasAnyFilter) {
          <button type="button" class="text-button" (click)="clearAllFilters()">
            {{ 'TABLE.ACTIONS.CLEAR_FILTERS' | translate }}
          </button>
        }

        @if (canDownloadCsv) {
          <button
            type="button"
            class="icon-button"
            [title]="'TABLE.ACTIONS.DOWNLOAD_CSV' | translate"
            [attr.aria-label]="'TABLE.ACTIONS.DOWNLOAD_CSV' | translate"
            (click)="downloadCsv()"
          >
            <mat-icon>download</mat-icon>
          </button>
        }

        @if (actions.length > 0) {
          <button
            type="button"
            class="icon-button"
            [title]="'COMMON.ACTIONS' | translate"
            [attr.aria-label]="'COMMON.ACTIONS' | translate"
            aria-haspopup="true"
            [attr.aria-expanded]="isPopoverOpen(actionsPopover)"
            [class.active]="isPopoverOpen(actionsPopover)"
            (click)="togglePopover(actionsPopover)"
          >
            <mat-icon>more_horiz</mat-icon>
            @if (selectedCount > 0) {
              <span class="badge" aria-hidden="true">{{ selectedCount }}</span>
            }
          </button>

          @if (isPopoverOpen(actionsPopover)) {
            <div class="popover actions-popover" role="group" [attr.aria-label]="'COMMON.ACTIONS' | translate" (keydown.escape)="closePopover()">
              <div class="popover-title">{{ 'COMMON.ACTIONS' | translate }}</div>
              @for (action of actions; track action.id) {
                <div
                  class="option"
                  role="button"
                  tabindex="0"
                  [class.danger]="action.danger"
                  [class.disabled]="isActionDisabled(action)"
                  [attr.aria-disabled]="isActionDisabled(action)"
                  (click)="runAction(action)"
                  (keydown.enter)="runAction(action)"
                  (keydown.space)="$event.preventDefault(); runAction(action)"
                >
                  <mat-icon>{{ action.icon }}</mat-icon>
                  <span class="option-label">{{ actionLabel(action) }}</span>
                </div>
              }
            </div>
          }
        }
      </div>
    </div>
  }

  @if (isLoading) {
    <div class="loading-status" role="status">{{ 'TABLE.LOADING' | translate }}</div>
  }

  <div class="table-scroll" [class.resizing]="isResizing">
    <table [attr.aria-label]="label ? (label | translate) : null">
      <colgroup>
        @if (selectable) {
          <col class="select-col" />
        }
        @for (header of table.getFlatHeaders(); track header.id) {
          <col [style.width.px]="columnWidth(header)" />
        }
        <!-- Absorbs whatever the sized columns leave over, which is what stops the browser widening them to fill the card. -->
        <col />
        @if (hasRowActions) {
          <col class="row-actions-col" />
        }
      </colgroup>

      <thead>
        <tr>
          @if (selectable) {
            <th class="select-cell" scope="col">
              <span
                class="checkbox"
                role="checkbox"
                tabindex="0"
                [class.checked]="table.getIsAllRowsSelected() || table.getIsSomeRowsSelected()"
                [attr.aria-checked]="table.getIsAllRowsSelected() ? 'true' : table.getIsSomeRowsSelected() ? 'mixed' : 'false'"
                [attr.aria-label]="'TABLE.SELECT_ALL_ROWS' | translate"
                (click)="table.toggleAllRowsSelected()"
                (keydown.enter)="table.toggleAllRowsSelected()"
                (keydown.space)="$event.preventDefault(); table.toggleAllRowsSelected()"
              >
                @if (table.getIsAllRowsSelected()) {
                  <mat-icon>check</mat-icon>
                } @else if (table.getIsSomeRowsSelected()) {
                  <mat-icon>remove</mat-icon>
                }
              </span>
            </th>
          }
          @for (header of table.getFlatHeaders(); track header.id) {
            <th scope="col" [attr.data-column-id]="header.column.id" [attr.aria-sort]="header.column.getCanSort() ? sortAriaState(header.column) : null">
              <div class="header-content">
                <span class="header-label">{{ headerLabel(header.column) }}</span>
                @if (header.column.columnDef.meta?.filter) {
                  <button
                    type="button"
                    class="icon-button small"
                    [title]="'TABLE.FILTER_BY' | translate: { column: headerLabel(header.column) }"
                    [attr.aria-label]="'TABLE.FILTER_BY' | translate: { column: headerLabel(header.column) }"
                    aria-haspopup="true"
                    [attr.aria-expanded]="isPopoverOpen(filterPopoverKey(header.column))"
                    [class.active]="isPopoverOpen(filterPopoverKey(header.column)) || header.column.getIsFiltered()"
                    (click)="togglePopover(filterPopoverKey(header.column))"
                  >
                    <mat-icon>filter_list</mat-icon>
                    @if (header.column.getIsFiltered()) {
                      @if (isFacetFilter(header.column)) {
                        <span class="badge" aria-hidden="true">{{ activeFilterCount(header.column) }}</span>
                      } @else {
                        <span class="filter-dot" aria-hidden="true"></span>
                      }
                    }
                  </button>
                }
                @if (header.column.getCanSort()) {
                  <button
                    type="button"
                    class="icon-button small"
                    [title]="'TABLE.SORT_HINT' | translate"
                    [attr.aria-label]="'TABLE.SORT_BY' | translate: { column: headerLabel(header.column) }"
                    [class.active]="!!header.column.getIsSorted()"
                    (click)="toggleSorting(header.column)"
                  >
                    <mat-icon>{{ sortIcon(header.column) }}</mat-icon>
                  </button>
                }
              </div>

              @if (header.column.columnDef.meta?.headerTemplate; as headerTemplate) {
                <div class="header-summary">
                  <ng-container
                    [ngTemplateOutlet]="headerTemplate"
                    [ngTemplateOutletContext]="header.column.columnDef.meta?.templateContext ?? {}"
                  ></ng-container>
                </div>
              }

              @if (header.column.getCanResize()) {
                <span
                  class="resize-grip"
                  role="separator"
                  tabindex="0"
                  aria-orientation="vertical"
                  [class.resizing]="header.column.getIsResizing()"
                  [title]="'TABLE.AUTO_FIT_HINT' | translate"
                  [attr.aria-label]="'TABLE.RESIZE_COLUMN' | translate: { column: headerLabel(header.column) }"
                  [attr.aria-valuenow]="header.getSize()"
                  [attr.aria-valuemin]="minWidthFor(header.column)"
                  [attr.aria-valuemax]="maxColumnWidth"
                  (mousedown)="startResize(header, $event)"
                  (touchstart)="startResize(header, $event)"
                  (dblclick)="autoFitColumn(header.column, $event)"
                  (keydown.arrowleft)="nudgeWidth(header.column, -resizeStep, $event)"
                  (keydown.arrowright)="nudgeWidth(header.column, resizeStep, $event)"
                  (keydown.home)="setWidth(header.column, minWidthFor(header.column), $event)"
                  (keydown.end)="setWidth(header.column, maxColumnWidth, $event)"
                ></span>
              }

              @if (isPopoverOpen(filterPopoverKey(header.column)) && header.column.columnDef.meta?.filter === 'date') {
                <div
                  class="popover date-popover"
                  role="radiogroup"
                  [style]="popoverStyle"
                  [attr.aria-label]="filterTitle(header.column)"
                  (keydown.escape)="closePopover()"
                >
                  <div class="popover-title">
                    {{ filterTitle(header.column) }}
                  </div>
                  <div class="date-options">
                    @for (preset of datePresets; track preset.id) {
                      <div
                        class="date-option"
                        role="radio"
                        tabindex="0"
                        [attr.aria-checked]="isDatePresetSelected(preset.id)"
                        (click)="setDatePreset(preset.id, header.column)"
                        (keydown.enter)="setDatePreset(preset.id, header.column)"
                        (keydown.space)="$event.preventDefault(); setDatePreset(preset.id, header.column)"
                      >
                        <span class="radio" aria-hidden="true" [class.checked]="isDatePresetSelected(preset.id)"></span>
                        <span class="option-label">{{ preset.labelKey | translate }}</span>
                      </div>
                    }
                  </div>

                  @if (isCustomDateRange) {
                    <div class="custom-range">
                      <label>
                        <span>{{ 'COMMON.START_DATE' | translate }}</span>
                        <input type="date" [value]="customFrom" (change)="setCustomDateEnd('from', $event, header.column)" />
                      </label>
                      <label>
                        <span>{{ 'COMMON.END_DATE' | translate }}</span>
                        <input type="date" [value]="customTo" (change)="setCustomDateEnd('to', $event, header.column)" />
                      </label>
                    </div>
                  }

                  @if (header.column.getIsFiltered()) {
                    <div class="popover-footer">
                      <button type="button" class="text-button" (click)="resetFilter(header.column)">{{ 'COMMON.RESET' | translate }}</button>
                    </div>
                  }
                </div>
              }

              @if (isPopoverOpen(filterPopoverKey(header.column)) && isValueFilter(header.column)) {
                <div
                  class="popover filter-popover"
                  role="group"
                  [style]="popoverStyle"
                  [attr.aria-label]="filterTitle(header.column)"
                  (keydown.escape)="closePopover()"
                >
                  <div class="popover-title">
                    {{ filterTitle(header.column) }}
                  </div>
                  @if (!isFacetFilter(header.column) || header.column.columnDef.meta?.facetSearch !== false) {
                    <div class="search">
                      <mat-icon>search</mat-icon>
                      <input
                        type="text"
                        autofocus
                        [attr.inputmode]="isNumericColumn(header.column) ? 'decimal' : null"
                        [attr.aria-label]="filterPlaceholder(header.column)"
                        [placeholder]="filterPlaceholder(header.column)"
                        [value]="searchValue(header.column)"
                        (input)="search(header.column, $event); onTextFilterInput(header.column)"
                        (keydown.enter)="applyValueFilter(header.column)"
                      />
                    </div>
                  }

                  @if (isFacetFilter(header.column)) {
                    <div class="options">
                      @if (facetStatus === 'loading') {
                        <div class="facet-status"><app-spinner size="small"></app-spinner></div>
                      } @else if (facetStatus === 'error') {
                        <div class="facet-status error">{{ 'TABLE.FACETS_ERROR' | translate }}</div>
                      } @else {
                        @for (option of filterOptions; track option) {
                          <div
                            class="option"
                            role="checkbox"
                            tabindex="0"
                            [attr.aria-checked]="isOptionSelected(option)"
                            (click)="toggleOption(option); onFacetOptionToggled(header.column)"
                            (keydown.enter)="toggleOption(option); onFacetOptionToggled(header.column)"
                            (keydown.space)="$event.preventDefault(); toggleOption(option); onFacetOptionToggled(header.column)"
                          >
                            <span class="checkbox" aria-hidden="true" [class.checked]="isOptionSelected(option)">
                              @if (isOptionSelected(option)) {
                                <mat-icon>check</mat-icon>
                              }
                            </span>
                            <span class="option-label" [class.numeric]="header.column.columnDef.meta?.numeric">{{
                              facetOptionLabel(header.column, $any(option))
                            }}</span>
                          </div>
                        } @empty {
                          @if (hasOptionSearch) {
                            <div class="no-matches">{{ 'TABLE.NO_MATCHES' | translate: { search: optionSearch } }}</div>
                          }
                        }
                      }
                    </div>
                  }

                  <div class="popover-footer">
                    @if (selectedOptionCount > 0) {
                      <button type="button" class="text-button" (click)="unselectOptions()">
                        {{ 'TABLE.UNSELECT_COUNT' | translate: { count: selectedOptionCount } }}
                      </button>
                    }
                    @if (showSelectOptionsButton) {
                      <button type="button" class="text-button" (click)="selectOptions()">
                        {{ 'TABLE.SELECT_COUNT' | translate: { count: filterOptions.length } }}
                      </button>
                    }
                    @if (header.column.getIsFiltered()) {
                      <button type="button" class="text-button" (click)="resetFilter(header.column)">{{ 'COMMON.RESET' | translate }}</button>
                    }
                  </div>
                </div>
              }
            </th>
          }
          <th class="spacer" scope="col" aria-hidden="true"></th>
          @if (hasRowActions) {
            <th class="row-actions-cell" scope="col" [attr.aria-label]="'COMMON.ACTIONS' | translate"></th>
          }
        </tr>
      </thead>

      <tbody [attr.aria-busy]="isLoading || isRefetching ? 'true' : null" [class.refetching]="isRefetching">
        @if (isLoading) {
          @let skeletonColumns = table.getVisibleLeafColumns();
          @for (row of skeletonRows; track row) {
            <tr class="skeleton-row" aria-hidden="true">
              @if (selectable) {
                <td class="select-cell"></td>
              }
              @for (column of skeletonColumns; track column.id) {
                <td><ngx-skeleton-loader appearance="line" [theme]="skeletonTheme" /></td>
              }
              <td class="spacer"></td>
              @if (hasRowActions) {
                <td class="row-actions-cell"></td>
              }
            </tr>
          }
        } @else if (isError) {
          <tr class="status-row">
            <td [attr.colspan]="columnCount">
              <div class="status error">
                <mat-icon>error_outline</mat-icon>
                <span class="status-message">{{ 'TABLE.ERROR' | translate }}</span>
                <button type="button" class="accent-button" (click)="retry()">{{ 'TABLE.RETRY' | translate }}</button>
              </div>
            </td>
          </tr>
        } @else {
          @for (row of table.getRowModel().rows; track row.id) {
            <tr
              [class]="classForRow(row.original)"
              [class.selected]="highlightSelected && row.getIsSelected()"
              [class.activatable]="rowsAreActivatable"
              [attr.role]="rowsAreActivatable ? 'button' : null"
              [attr.tabindex]="rowsAreActivatable ? 0 : null"
              (click)="activateRow(row.original, $event)"
              (keydown.enter)="activateRow(row.original, $event)"
              (keydown.space)="$event.preventDefault(); activateRow(row.original, $event)"
            >
              @if (selectable) {
                <td class="select-cell">
                  <span
                    class="checkbox"
                    role="checkbox"
                    tabindex="0"
                    [class.checked]="row.getIsSelected()"
                    [attr.aria-checked]="row.getIsSelected()"
                    [attr.aria-label]="'TABLE.SELECT_ROW' | translate"
                    (click)="row.toggleSelected()"
                    (keydown.enter)="row.toggleSelected()"
                    (keydown.space)="$event.preventDefault(); row.toggleSelected()"
                  >
                    @if (row.getIsSelected()) {
                      <mat-icon>check</mat-icon>
                    }
                  </span>
                </td>
              }
              @for (cell of row.getVisibleCells(); track cell.id) {
                <td [class]="cell.column.columnDef.meta?.tone" [class.numeric]="cell.column.columnDef.meta?.numeric">
                  @if (cell.column.columnDef.meta?.cellTemplate; as cellTemplate) {
                    <ng-container [ngTemplateOutlet]="cellTemplate" [ngTemplateOutletContext]="cellContext(row.original, cell.column)"></ng-container>
                  } @else if (chipFor(cell); as chip) {
                    <span class="status-chip" [class]="'chip-' + chip.variant">{{ chip.label }}</span>
                  } @else {
                    <ng-container *flexRender="cell.column.columnDef.cell; props: cell.getContext(); let cellValue">{{ cellValue }}</ng-container>
                  }
                </td>
              }
              <td class="spacer"></td>
              @if (hasRowActions) {
                <td class="row-actions-cell" [class.menu-open]="isPopoverOpen(rowActionsPopover(row.original))">
                  @if (rowActionsFor(row.original).length > 0) {
                    <button
                      type="button"
                      class="icon-button"
                      [title]="'COMMON.ACTIONS' | translate"
                      [attr.aria-label]="'COMMON.ACTIONS' | translate"
                      aria-haspopup="true"
                      [attr.aria-expanded]="isPopoverOpen(rowActionsPopover(row.original))"
                      [class.active]="isPopoverOpen(rowActionsPopover(row.original))"
                      (click)="togglePopover(rowActionsPopover(row.original))"
                    >
                      <mat-icon>more_vert</mat-icon>
                    </button>

                    @if (isPopoverOpen(rowActionsPopover(row.original))) {
                      <div
                        class="popover actions-popover row-actions-popover"
                        role="group"
                        [style]="popoverStyle"
                        [attr.aria-label]="'COMMON.ACTIONS' | translate"
                        (keydown.escape)="closePopover()"
                      >
                        @for (action of rowActionsFor(row.original); track action.id) {
                          <div
                            class="option"
                            role="button"
                            tabindex="0"
                            [class.danger]="action.danger"
                            [class.disabled]="isRowActionDisabled(action, row.original)"
                            [attr.aria-disabled]="isRowActionDisabled(action, row.original)"
                            (click)="runRowAction(action, row.original)"
                            (keydown.enter)="runRowAction(action, row.original)"
                            (keydown.space)="$event.preventDefault(); runRowAction(action, row.original)"
                          >
                            <mat-icon>{{ action.icon }}</mat-icon>
                            <span class="option-label">{{ rowActionLabel(action, row.original) }}</span>
                          </div>
                        }
                      </div>
                    }
                  }
                </td>
              }
            </tr>
          } @empty {
            <tr class="status-row">
              <td [attr.colspan]="columnCount">
                <div class="status empty">
                  <mat-icon>search_off</mat-icon>
                  <span class="status-message">{{ 'TABLE.EMPTY' | translate }}</span>
                </div>
              </td>
            </tr>
          }
        }
      </tbody>
    </table>
  </div>

  @if (showPagination) {
    <div class="pagination">
      <div class="pagination-start">
        <div class="page-sizes" role="group" [attr.aria-label]="'TABLE.ROWS_PER_PAGE' | translate">
          @for (size of pageSizeOptions; track size) {
            <button type="button" class="page-size-button" [class.active]="isPageSize(size)" [attr.aria-pressed]="isPageSize(size)" (click)="setPageSize(size)">
              {{ size }}
            </button>
          }
        </div>
        <div class="summary" aria-live="polite">
          @if (isLoading) {
            <ngx-skeleton-loader appearance="line" [theme]="skeletonTheme" />
          } @else {
            {{ paginationSummary }}
          }
        </div>
      </div>
      <div class="pages">
        <span class="page-label">
          @if (isLoading) {
            <ngx-skeleton-loader appearance="line" [theme]="skeletonTheme" />
          } @else {
            {{ 'TABLE.PAGE_OF' | translate: { page: currentPageIndex + 1, pageCount: table.getPageCount() } }}
          }
        </span>
        <button
          type="button"
          class="icon-button page-button"
          [title]="'TABLE.PREVIOUS_PAGE' | translate"
          [attr.aria-label]="'TABLE.PREVIOUS_PAGE' | translate"
          [disabled]="!table.getCanPreviousPage()"
          (click)="table.previousPage()"
        >
          <mat-icon>chevron_left</mat-icon>
        </button>
        <button
          type="button"
          class="icon-button page-button"
          [title]="'TABLE.NEXT_PAGE' | translate"
          [attr.aria-label]="'TABLE.NEXT_PAGE' | translate"
          [disabled]="!table.getCanNextPage()"
          (click)="table.nextPage()"
        >
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>
    </div>
  }
</div>

@if (anyPopoverOpen) {
  <div class="popover-backdrop" (click)="closePopover()"></div>
}
